import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faPlus,
  faTrash,
  faArrowUp,
  faArrowDown,
  faBookmark,
  faClipboardList,
  faInbox,
  faPaperPlane,
  faSearch,
  faCircleInfo,
  faUserPlus,
  faChevronDown,
  faChevronUp,
} from '@fortawesome/free-solid-svg-icons';
import {
  Survey,
  SurveyDeliveryMode,
  SurveyQuestion,
  SurveyQuestionType,
  SurveyRecurringDays,
  SurveyStatus,
  SurveyWhen,
  SurveyDeploymentGroup,
  SurveyPaceMode,
  SurveyTargetedUser,
  User,
} from '../../types';
import { API_BASE_URL } from '../../config/api';
import {
  SURVEY_QUESTION_TYPE_OPTIONS,
  SURVEY_RECURRING_DAY_OPTIONS,
  SURVEY_STATUS_OPTIONS,
  SurveyFormState,
  emptySurveyForm,
  emptySurveyQuestion,
  formFromSurvey,
  parseOptionalPositiveInt,
  SURVEY_DEPLOYMENT_GROUP_OPTIONS,
  SURVEY_MOBILE_PLATFORM_OPTIONS,
} from './surveyUtils';

const reportStatusBadge = (status?: string) => {
  if (status === 'completed') return <span className="badge text-bg-success">Completed</span>;
  if (status === 'opted_out') return <span className="badge text-bg-secondary">Opted out</span>;
  return <span className="badge text-bg-warning">Pending</span>;
};

const displayName = (user: { firstName?: string; lastName?: string; email?: string }) =>
  `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unnamed user';

// Reusable user search + add control, used for targeted users. Manages its own
// search state so it can be dropped in anywhere.
const UserSearchAdder: React.FC<{
  onAdd: (user: User) => void;
  isAdded: (user: User) => boolean;
  canAddMore: boolean;
  addLabel?: string;
}> = ({ onAdd, isAdded, canAddMore, addLabel = 'Add' }) => {
  const [search, setSearch] = useState({ email: '', firstName: '', lastName: '' });
  const [results, setResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async () => {
    const email = search.email.trim();
    const firstName = search.firstName.trim();
    const lastName = search.lastName.trim();
    if (!email && !firstName && !lastName) {
      setError('Enter an email, first name, or last name to search.');
      setResults([]);
      return;
    }
    setSearching(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { limit: 10, sortColumn: 'email' };
      if (email) params.email = email;
      if (firstName) params.firstName = firstName;
      if (lastName) params.lastName = lastName;
      const response = await axios.get(`${API_BASE_URL}/users`, { withCredentials: true, params });
      setResults(response.data.users || []);
      if (!(response.data.users || []).length) {
        setError('No users found matching that search.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to search users');
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  return (
    <div>
      {error && <div className="alert alert-danger py-2 mb-3">{error}</div>}
      <div className="row g-2 align-items-end mb-3">
        <div className="col-md-4">
          <label className="form-label small fw-bold">Email</label>
          <input
            type="text"
            className="form-control"
            value={search.email}
            onChange={(e) => setSearch((prev) => ({ ...prev, email: e.target.value }))}
            placeholder="name@example.com"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small fw-bold">First Name</label>
          <input
            type="text"
            className="form-control"
            value={search.firstName}
            onChange={(e) => setSearch((prev) => ({ ...prev, firstName: e.target.value }))}
          />
        </div>
        <div className="col-md-3">
          <label className="form-label small fw-bold">Last Name</label>
          <input
            type="text"
            className="form-control"
            value={search.lastName}
            onChange={(e) => setSearch((prev) => ({ ...prev, lastName: e.target.value }))}
          />
        </div>
        <div className="col-md-2 d-grid">
          <button type="button" className="btn btn-outline-secondary" onClick={runSearch} disabled={searching}>
            <FontAwesomeIcon icon={faSearch} className="me-1" />
            {searching ? 'Searching…' : 'Find'}
          </button>
        </div>
      </div>

      {results.length > 0 && (
        <div className="table-responsive mb-0">
          <table className="table table-sm align-middle mb-0">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {results.map((user) => {
                const added = isAdded(user);
                return (
                  <tr key={user._id}>
                    <td>{displayName(user)}</td>
                    <td>{user.email}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => onAdd(user)}
                        disabled={added || !canAddMore}
                      >
                        {added ? 'Added' : addLabel}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const SurveyEditorPage: React.FC = () => {
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const isNew = !surveyId || surveyId === 'new';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState<SurveyFormState>(emptySurveyForm());
  const [editingSurvey, setEditingSurvey] = useState<Survey | null>(null);
  const [library, setLibrary] = useState<SurveyQuestion[]>([]);
  const [libraryPickId, setLibraryPickId] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testUserSearch, setTestUserSearch] = useState({ email: '', firstName: '', lastName: '' });
  const [testUserResults, setTestUserResults] = useState<User[]>([]);
  const [selectedTestUsers, setSelectedTestUsers] = useState<User[]>([]);
  const [searchingTestUsers, setSearchingTestUsers] = useState(false);
  const [deployingTestSurvey, setDeployingTestSurvey] = useState(false);
  const [testDeployError, setTestDeployError] = useState<string | null>(null);
  const [testDeployMessage, setTestDeployMessage] = useState<string | null>(null);
  const [targetedUsers, setTargetedUsers] = useState<SurveyTargetedUser[]>([]);
  const [targetedOpen, setTargetedOpen] = useState(false);
  const [targetedError, setTargetedError] = useState<string | null>(null);
  const [deviceModelOptions, setDeviceModelOptions] = useState<string[]>([]);

  const MAX_TARGETED_USERS = 10;

  const canEditDetails = isNew || !editingSurvey || editingSurvey.status === 'planning' || editingSurvey.status === 'paused';

  const fetchLibrary = useCallback(async () => {
    const response = await axios.get(`${API_BASE_URL}/surveys/questions`, {
      withCredentials: true,
    });
    return (response.data.questions || []) as SurveyQuestion[];
  }, []);

  const loadPage = useCallback(async () => {
    setLoading(true);
    try {
      const questions = await fetchLibrary();
      setLibrary(questions);

      if (isNew || !surveyId) {
        setEditingSurvey(null);
        setForm(emptySurveyForm(questions.filter((question) => question.isDefault)));
        setTargetedUsers([]);
        setError(null);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/surveys/${surveyId}`, {
        withCredentials: true,
      });
      const survey = response.data.survey as Survey;
      setEditingSurvey(survey);
      setForm(formFromSurvey(survey));
      setTargetedUsers(survey.targetedUsers || []);
      setError(null);
    } catch (err: any) {
      console.error('Error loading survey:', err);
      setError(err.response?.data?.message || 'Failed to load survey');
      setEditingSurvey(null);
    } finally {
      setLoading(false);
    }
  }, [fetchLibrary, isNew, surveyId]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    const loadModels = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/models`, { withCredentials: true });
        const names = ((response.data.models || []) as { name?: string }[])
          .map((model) => String(model.name || '').trim())
          .filter(Boolean);
        setDeviceModelOptions(names);
      } catch (err) {
        console.error('Error loading device models:', err);
      }
    };
    loadModels();
  }, []);

  const usedQuestionIds = useMemo(
    () => new Set(form.questions.map((question) => question._id).filter(Boolean) as string[]),
    [form.questions]
  );

  const unusedLibraryQuestions = useMemo(
    () => library.filter((question) => question._id && !usedQuestionIds.has(question._id)),
    [library, usedQuestionIds]
  );

  const updateQuestion = (index: number, patch: Partial<SurveyQuestion>) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => (i === index ? { ...question, ...patch } : question)),
    }));
  };

  const addQuestion = () => {
    setForm((prev) => ({ ...prev, questions: [...prev.questions, emptySurveyQuestion()] }));
  };

  const addFromLibrary = () => {
    const selected = library.find((question) => question._id === libraryPickId);
    if (!selected || !selected._id || usedQuestionIds.has(selected._id)) return;
    setForm((prev) => ({
      ...prev,
      questions: [...prev.questions, {
        ...selected,
        choices: selected.choices?.length ? selected.choices : [''],
        additionalInfo: selected.additionalInfo || '',
        isDefault: Boolean(selected.isDefault),
      }],
    }));
    setLibraryPickId('');
  };

  const removeQuestion = (index: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index),
    }));
  };

  const moveQuestion = (index: number, direction: -1 | 1) => {
    const nextIndex = index + direction;
    setForm((prev) => {
      if (nextIndex < 0 || nextIndex >= prev.questions.length) return prev;
      const questions = [...prev.questions];
      const [moved] = questions.splice(index, 1);
      questions.splice(nextIndex, 0, moved);
      return { ...prev, questions };
    });
  };

  const updateQuestionChoice = (questionIndex: number, choiceIndex: number, value: string) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => {
        if (i !== questionIndex) return question;
        const choices = [...(question.choices || [])];
        choices[choiceIndex] = value;
        return { ...question, choices };
      }),
    }));
  };

  const addQuestionChoice = (questionIndex: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => (
        i === questionIndex
          ? { ...question, choices: [...(question.choices || []), ''] }
          : question
      )),
    }));
  };

  const removeQuestionChoice = (questionIndex: number, choiceIndex: number) => {
    setForm((prev) => ({
      ...prev,
      questions: prev.questions.map((question, i) => {
        if (i !== questionIndex) return question;
        const choices = (question.choices || []).filter((_, ci) => ci !== choiceIndex);
        return { ...question, choices: choices.length ? choices : [''] };
      }),
    }));
  };

  const searchTestUsers = async () => {
    const params: Record<string, string | number> = {
      limit: 10,
      sortColumn: 'email',
    };
    const email = testUserSearch.email.trim();
    const firstName = testUserSearch.firstName.trim();
    const lastName = testUserSearch.lastName.trim();
    if (email) params.email = email;
    if (firstName) params.firstName = firstName;
    if (lastName) params.lastName = lastName;
    if (!email && !firstName && !lastName) {
      setTestDeployError('Enter an email, first name, or last name to search.');
      setTestUserResults([]);
      return;
    }

    setSearchingTestUsers(true);
    setTestDeployError(null);
    setTestDeployMessage(null);
    try {
      const response = await axios.get(`${API_BASE_URL}/users`, {
        withCredentials: true,
        params,
      });
      setTestUserResults(response.data.users || []);
      if (!(response.data.users || []).length) {
        setTestDeployError('No users found matching that search.');
      }
    } catch (err: any) {
      setTestDeployError(err.response?.data?.message || 'Failed to search users');
      setTestUserResults([]);
    } finally {
      setSearchingTestUsers(false);
    }
  };

  const attachTestUser = (user: User) => {
    setTestDeployError(null);
    setTestDeployMessage(null);
    setSelectedTestUsers((prev) => {
      if (prev.some((entry) => entry._id === user._id)) return prev;
      if (prev.length >= 5) {
        setTestDeployError('You can attach up to 5 users.');
        return prev;
      }
      return [...prev, user];
    });
  };

  const removeTestUser = (userId: string) => {
    setSelectedTestUsers((prev) => prev.filter((user) => user._id !== userId));
  };

  const addTargetedUser = (user: User) => {
    setTargetedError(null);
    setTargetedUsers((prev) => {
      if (prev.some((entry) => entry.user === user._id)) return prev;
      if (prev.length >= MAX_TARGETED_USERS) {
        setTargetedError(`You can target up to ${MAX_TARGETED_USERS} users.`);
        return prev;
      }
      return [
        ...prev,
        {
          user: user._id,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          reportStatus: 'pending',
        },
      ];
    });
  };

  const removeTargetedUser = (userId: string) => {
    setTargetedUsers((prev) => prev.filter((entry) => entry.user !== userId));
  };

  const deployTestSurvey = async () => {
    if (!editingSurvey) return;
    if (!selectedTestUsers.length) {
      setTestDeployError('Attach at least one user first.');
      return;
    }

    setDeployingTestSurvey(true);
    setTestDeployError(null);
    setTestDeployMessage(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/surveys/${editingSurvey._id}/test-deploy`,
        { userIds: selectedTestUsers.map((user) => user._id) },
        { withCredentials: true }
      );
      setTestDeployMessage(response.data.message || 'Test survey deployed.');
      setSelectedTestUsers([]);
    } catch (err: any) {
      setTestDeployError(err.response?.data?.message || 'Failed to deploy test survey');
    } finally {
      setDeployingTestSurvey(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = form.name.trim();
    if (canEditDetails && !name) {
      setFormError('Name is required');
      return;
    }

    const maxResponses = parseOptionalPositiveInt(form.maxResponses);
    const maxImpressions = parseOptionalPositiveInt(form.maxImpressions);
    const perDay = parseOptionalPositiveInt(form.perDay);
    if (maxResponses === undefined) {
      setFormError('Max number of responses must be a positive whole number');
      return;
    }
    if (maxImpressions === undefined) {
      setFormError('Max number of impressions must be a positive whole number');
      return;
    }
    if (form.paceMode === 'per_day' && !perDay) {
      setFormError('Per day must be a positive whole number');
      return;
    }

    setSaving(true);
    setFormError(null);

    const questions = form.questions
      .map((question) => ({
        _id: question._id,
        prompt: question.prompt.trim(),
        type: question.type,
        additionalInfo: (question.additionalInfo || '').trim(),
        isDefault: Boolean(question.isDefault),
        choices: question.type === 'choice'
          ? (question.choices || []).map((choice) => choice.trim()).filter(Boolean)
          : [],
      }))
      .filter((question) => question.prompt || question._id);

    const targetedUserIds = targetedUsers.map((entry) => entry.user);
    const payload = canEditDetails
      ? {
          name,
          summary: form.summary,
          status: form.status,
          deliveryMode: form.deliveryMode,
          when: form.when,
          recurringDays: form.recurringDays,
          audienceMode: form.audienceMode,
          deploymentGroups: form.deploymentGroups,
          deviceModels: form.deviceModels,
          mobilePlatforms: form.mobilePlatforms,
          targetedUsers: targetedUserIds,
          maxResponses,
          maxImpressions,
          paceMode: form.paceMode,
          perDay: perDay || 20,
          questions,
        }
      : {
          summary: form.summary,
          status: form.status,
          deliveryMode: form.deliveryMode,
          when: form.when,
          recurringDays: form.recurringDays,
          audienceMode: form.audienceMode,
          deploymentGroups: form.deploymentGroups,
          deviceModels: form.deviceModels,
          mobilePlatforms: form.mobilePlatforms,
          targetedUsers: targetedUserIds,
          maxResponses,
          maxImpressions,
          paceMode: form.paceMode,
          perDay: perDay || 20,
          questions,
        };

    try {
      if (editingSurvey) {
        const response = await axios.patch(`${API_BASE_URL}/surveys/${editingSurvey._id}`, payload, {
          withCredentials: true,
        });
        const survey = response.data.survey as Survey;
        setEditingSurvey(survey);
        setForm(formFromSurvey(survey));
        setTargetedUsers(survey.targetedUsers || []);
        setLibrary(await fetchLibrary());
      } else {
        const response = await axios.post(`${API_BASE_URL}/surveys`, payload, { withCredentials: true });
        const survey = response.data.survey as Survey;
        navigate(`/surveys/${survey._id}`, { replace: true });
      }
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to save survey');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editingSurvey) return;
    if (!window.confirm(`Delete survey "${editingSurvey.name}"?`)) return;

    setDeleting(true);
    setFormError(null);
    try {
      await axios.delete(`${API_BASE_URL}/surveys/${editingSurvey._id}`, {
        withCredentials: true,
      });
      navigate('/surveys');
    } catch (err: any) {
      setFormError(err.response?.data?.message || 'Failed to delete survey');
      setDeleting(false);
    }
  };

  const pageTitle = isNew ? 'New Survey' : 'Edit Survey';

  return (
    <div className="container-fluid mt-3 mb-5">
      <div className="mb-3">
        <Link to="/surveys" className="btn btn-sm btn-outline-secondary">
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Link>
      </div>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '180px' }}>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : (
        <form onSubmit={handleSave}>
          <h2 className="d-flex align-items-center gap-2 mb-3">
            <FontAwesomeIcon icon={faClipboardList} className="text-primary survey-page-title-icon" />
            <span>
              {pageTitle}
              {form.name.trim() ? <span className="fw-normal"> - {form.name.trim()}</span> : null}
            </span>
          </h2>

          {formError && <div className="alert alert-danger">{formError}</div>}

          {!canEditDetails && (
            <div className="alert alert-info d-flex align-items-start gap-2">
              <FontAwesomeIcon icon={faCircleInfo} className="mt-1" />
              <div>
                Existing questions stay locked after a survey leaves Planning. You can still change the summary,
                who / when / how many / rollout, targeted users, and add questions.
              </div>
            </div>
          )}

          {editingSurvey && editingSurvey.status !== 'planning' && (
            <div className="card shadow-sm border-0 mb-4">
              <div className="card-body d-flex flex-wrap align-items-center justify-content-between gap-2">
                <div>
                  <h5 className="card-title mb-1">Results</h5>
                  <div className="text-muted">
                    {editingSurvey.resultCount || 0} result{(editingSurvey.resultCount || 0) === 1 ? '' : 's'} received
                  </div>
                </div>
                <Link
                  to={`/surveys/${editingSurvey._id}/results`}
                  className="btn btn-primary btn-sm"
                >
                  <FontAwesomeIcon icon={faInbox} className="me-1" />
                  Open results
                </Link>
              </div>
            </div>
          )}

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">Details</h5>

              <div className="mb-3">
                <label htmlFor="survey-name" className="form-label fw-bold">Name</label>
                <input
                  id="survey-name"
                  type="text"
                  className="form-control"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                  required={canEditDetails}
                  disabled={!canEditDetails}
                />
              </div>

              <div className="mb-3">
                <label htmlFor="survey-summary" className="form-label fw-bold">Summary</label>
                <textarea
                  id="survey-summary"
                  className="form-control"
                  rows={3}
                  value={form.summary}
                  onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
                  placeholder="Short description of this survey"
                />
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label htmlFor="survey-status" className="form-label fw-bold">Status</label>
                  <select
                    id="survey-status"
                    className="form-select"
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value as SurveyStatus }))}
                  >
                    {SURVEY_STATUS_OPTIONS
                      .filter((option) => canEditDetails || option.value !== 'planning')
                      .map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                  </select>
                </div>
              </div>

            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <h5 className="card-title mb-3">Deployment</h5>

              <div className="row g-3 mb-3 align-items-start">
                <div className="col-md-6">
                  <div className="survey-deploy-box">
                    <div className="survey-deploy-box-title">Audience</div>
                    <div className="btn-group btn-group-sm" role="group" aria-label="Audience mode">
                      <button
                        type="button"
                        className={`btn ${form.audienceMode === 'device' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForm((prev) => ({ ...prev, audienceMode: 'device' }))}
                      >
                        By Mobile Device ID
                      </button>
                      <button
                        type="button"
                        className={`btn ${form.audienceMode === 'account' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForm((prev) => ({ ...prev, audienceMode: 'account' }))}
                      >
                        By User Account
                      </button>
                    </div>
                    <div className="form-text mt-2">
                      {form.audienceMode === 'device'
                        ? 'Show to any app install. Completions, limits, and timing are tracked by device ID, so people do not need an account.'
                        : 'Limit to logged-in testers or accounts with registered devices. People without an account will not see this survey.'}
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="survey-deploy-box">
                    <div className="survey-deploy-box-title">Mobile Platform</div>
                    <div className="form-text mb-2">
                      Applies to both audience modes. Leave both checked to send to every phone.
                    </div>
                    {SURVEY_MOBILE_PLATFORM_OPTIONS.map((option) => {
                      const allSelected = form.mobilePlatforms.length === 0;
                      const checked = allSelected || form.mobilePlatforms.includes(option.value);
                      return (
                        <div className="form-check" key={option.value}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`survey-platform-${option.value}`}
                            checked={checked}
                            onChange={() => {
                              setForm((prev) => {
                                const currentlyAll = prev.mobilePlatforms.length === 0;
                                const currentlyChecked = currentlyAll || prev.mobilePlatforms.includes(option.value);
                                const catalog = SURVEY_MOBILE_PLATFORM_OPTIONS.map((entry) => entry.value);
                                let next = currentlyAll ? catalog : [...prev.mobilePlatforms];
                                if (currentlyChecked) {
                                  next = next.filter((platform) => platform !== option.value);
                                } else {
                                  next = [...next, option.value];
                                }
                                next = catalog.filter((platform) => next.includes(platform));
                                if (!next.length) return prev;
                                return {
                                  ...prev,
                                  mobilePlatforms: next.length === catalog.length ? [] : next,
                                };
                              });
                            }}
                          />
                          <label className="form-check-label" htmlFor={`survey-platform-${option.value}`}>
                            {option.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {form.audienceMode === 'account' && (
              <div className="row g-3 mb-3 align-items-start">
                <div className="col-md-6">
                  <div className="survey-deploy-box">
                    <div className="survey-deploy-box-title">Group</div>
                    <div className="form-text mb-2">
                      Internal Testing and Beta only show to logged-in matching testers. Production shows to everyone else, including logged-out users.
                    </div>
                    {SURVEY_DEPLOYMENT_GROUP_OPTIONS.map((option) => {
                      const checked = form.deploymentGroups.includes(option.value);
                      return (
                        <div className="form-check" key={option.value}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id={`survey-group-${option.value}`}
                            checked={checked}
                            onChange={() => {
                              setForm((prev) => {
                                const next = checked
                                  ? prev.deploymentGroups.filter((group) => group !== option.value)
                                  : [...prev.deploymentGroups, option.value];
                                return {
                                  ...prev,
                                  deploymentGroups: next.length
                                    ? next as SurveyDeploymentGroup[]
                                    : prev.deploymentGroups,
                                };
                              });
                            }}
                          />
                          <label className="form-check-label" htmlFor={`survey-group-${option.value}`}>
                            {option.label}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="survey-deploy-box">
                    <div className="survey-deploy-box-title">Device</div>
                    <div className="form-text mb-2">
                      Only users with a registered device of a checked model will see this survey. Leave all checked to send to every model.
                    </div>
                    {deviceModelOptions.length === 0 ? (
                      <div className="text-muted small">Loading models…</div>
                    ) : (
                      deviceModelOptions.map((model) => {
                        const allSelected = form.deviceModels.length === 0;
                        const checked = allSelected || form.deviceModels.includes(model);
                        return (
                          <div className="form-check" key={model}>
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={`survey-device-${model}`}
                              checked={checked}
                              onChange={() => {
                                setForm((prev) => {
                                  const currentlyAll = prev.deviceModels.length === 0;
                                  const currentlyChecked = currentlyAll || prev.deviceModels.includes(model);
                                  let next = currentlyAll ? deviceModelOptions : [...prev.deviceModels];
                                  if (currentlyChecked) {
                                    next = next.filter((name) => name !== model);
                                  } else {
                                    next = [...next, model];
                                  }
                                  next = deviceModelOptions.filter((name) => next.includes(name));
                                  if (!next.length) return prev;
                                  return {
                                    ...prev,
                                    deviceModels: next.length === deviceModelOptions.length ? [] : next,
                                  };
                                });
                              }}
                            />
                            <label className="form-check-label" htmlFor={`survey-device-${model}`}>
                              {model}
                            </label>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
              )}

              <div className="row g-3 mb-4 align-items-start">
                <div className="col-lg-4">
                  <div className="survey-deploy-box">
                    <div className="survey-deploy-box-title">How Many</div>
                    <label htmlFor="survey-max-responses" className="form-label">
                      Max number of responses
                    </label>
                    <input
                      id="survey-max-responses"
                      type="number"
                      min={1}
                      step={1}
                      className="form-control form-control-sm"
                      value={form.maxResponses}
                      onChange={(e) => setForm((prev) => ({ ...prev, maxResponses: e.target.value }))}
                      placeholder="Unlimited"
                    />
                    <div className="form-text mb-3">Stop after this many completed surveys. Leave blank for no limit.</div>
                    <label htmlFor="survey-max-impressions" className="form-label">
                      Max number of impressions
                    </label>
                    <input
                      id="survey-max-impressions"
                      type="number"
                      min={1}
                      step={1}
                      className="form-control form-control-sm"
                      value={form.maxImpressions}
                      onChange={(e) => setForm((prev) => ({ ...prev, maxImpressions: e.target.value }))}
                      placeholder="Unlimited"
                    />
                    <div className="form-text">Stop after this many responses + ignores. Leave blank for no limit.</div>
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="survey-deploy-box">
                    <div className="survey-deploy-box-title">When</div>
                    <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Delivery mode">
                      <button
                        type="button"
                        className={`btn ${form.deliveryMode === 'once' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForm((prev) => ({ ...prev, deliveryMode: 'once' }))}
                      >
                        Once Only
                      </button>
                      <button
                        type="button"
                        className={`btn ${form.deliveryMode === 'recurring' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForm((prev) => ({ ...prev, deliveryMode: 'recurring' as SurveyDeliveryMode }))}
                      >
                        Ask Every X Days
                      </button>
                    </div>

                    {form.deliveryMode === 'once' && (
                      <div className="mt-2">
                        <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Timing">
                          <button
                            type="button"
                            className={`btn ${form.when === 'next_open' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setForm((prev) => ({ ...prev, when: 'next_open' }))}
                          >
                            Next Open
                          </button>
                          <button
                            type="button"
                            className={`btn ${form.when === 'after_open' ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => setForm((prev) => ({ ...prev, when: 'after_open' as SurveyWhen }))}
                          >
                            X days after next open
                          </button>
                        </div>
                        {form.when === 'after_open' && (
                          <div className="mt-2">
                            <select
                              className="form-select form-select-sm"
                              value={form.recurringDays}
                              onChange={(e) => setForm((prev) => ({
                                ...prev,
                                recurringDays: Number(e.target.value) as SurveyRecurringDays,
                              }))}
                              aria-label="Days after next open"
                            >
                              {SURVEY_RECURRING_DAY_OPTIONS.map((days) => (
                                <option key={days} value={days}>{days} days</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}

                    {form.deliveryMode === 'recurring' && (
                      <div className="mt-2">
                        <select
                          className="form-select form-select-sm"
                          value={form.recurringDays}
                          onChange={(e) => setForm((prev) => ({
                            ...prev,
                            recurringDays: Number(e.target.value) as SurveyRecurringDays,
                          }))}
                          aria-label="Ask every X days"
                        >
                          {SURVEY_RECURRING_DAY_OPTIONS.map((days) => (
                            <option key={days} value={days}>{days} days</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-lg-4">
                  <div className="survey-deploy-box">
                    <div className="survey-deploy-box-title">Rollout</div>
                    <div className="btn-group btn-group-sm flex-wrap" role="group" aria-label="Rollout pace">
                      <button
                        type="button"
                        className={`btn ${form.paceMode === 'all_at_once' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForm((prev) => ({ ...prev, paceMode: 'all_at_once' }))}
                      >
                        All At Once
                      </button>
                      <button
                        type="button"
                        className={`btn ${form.paceMode === 'per_day' ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setForm((prev) => ({ ...prev, paceMode: 'per_day' as SurveyPaceMode }))}
                      >
                        X Per Day
                      </button>
                    </div>
                    {form.paceMode === 'per_day' && (
                      <div className="mt-2">
                        <input
                          type="number"
                          min={1}
                          step={1}
                          className="form-control form-control-sm"
                          value={form.perDay}
                          onChange={(e) => setForm((prev) => ({ ...prev, perDay: e.target.value }))}
                          aria-label="Number of new surveys per day"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {form.audienceMode === 'account' && (
              <div className="mb-4">
                <label className="form-label fw-bold">Targeted Users</label>
                <div className="rounded-3 bg-light border p-3">
                  <div className="text-muted small mb-3">
                    Send to specific users on top of the groups above (up to {MAX_TARGETED_USERS}). They receive it even if their
                    group isn’t selected, and never twice. Report status updates after each save.
                  </div>

                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => setTargetedOpen((open) => !open)}
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                    Add targeted users ({targetedUsers.length}/{MAX_TARGETED_USERS})
                    <FontAwesomeIcon icon={targetedOpen ? faChevronUp : faChevronDown} className="ms-2" />
                  </button>

                  {targetedError && (
                    <div className="alert alert-danger py-2 mt-2 mb-0">{targetedError}</div>
                  )}

                  {targetedOpen && (
                    <div className="rounded-3 bg-white border p-3 mt-2">
                      <UserSearchAdder
                        onAdd={addTargetedUser}
                        isAdded={(user) => targetedUsers.some((entry) => entry.user === user._id)}
                        canAddMore={targetedUsers.length < MAX_TARGETED_USERS}
                      />
                    </div>
                  )}

                  {targetedUsers.length > 0 ? (
                    <div className="table-responsive mt-3">
                      <table className="table table-sm align-middle mb-0">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Email</th>
                            <th>Report</th>
                            <th className="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {targetedUsers.map((entry) => (
                            <tr key={entry.user}>
                              <td>{displayName(entry)}</td>
                              <td>{entry.email}</td>
                              <td>{reportStatusBadge(entry.reportStatus)}</td>
                              <td className="text-end">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => removeTargetedUser(entry.user)}
                                  aria-label={`Remove ${entry.email}`}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-muted small mt-2">No targeted users.</div>
                  )}
                </div>
              </div>
              )}

              {editingSurvey && (
                <div className="mb-4">
                  <label className="form-label fw-bold">Test Deploy</label>
                  <div className="rounded-3 bg-light border p-3">
                    <div className="text-muted small mb-3">
                      Send the last saved version of this survey immediately to phones logged in with selected user accounts.
                    </div>

                    {testDeployError && (
                      <div className="alert alert-danger py-2 mb-3">{testDeployError}</div>
                    )}
                    {testDeployMessage && (
                      <div className="alert alert-success py-2 mb-3">{testDeployMessage}</div>
                    )}

                    <div className="row g-2 align-items-end mb-3">
                      <div className="col-md-4">
                        <label className="form-label small fw-bold" htmlFor="survey-test-user-email">Email</label>
                        <input
                          id="survey-test-user-email"
                          type="text"
                          className="form-control"
                          value={testUserSearch.email}
                          onChange={(e) => setTestUserSearch((prev) => ({ ...prev, email: e.target.value }))}
                          placeholder="name@example.com"
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold" htmlFor="survey-test-user-first-name">First Name</label>
                        <input
                          id="survey-test-user-first-name"
                          type="text"
                          className="form-control"
                          value={testUserSearch.firstName}
                          onChange={(e) => setTestUserSearch((prev) => ({ ...prev, firstName: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label small fw-bold" htmlFor="survey-test-user-last-name">Last Name</label>
                        <input
                          id="survey-test-user-last-name"
                          type="text"
                          className="form-control"
                          value={testUserSearch.lastName}
                          onChange={(e) => setTestUserSearch((prev) => ({ ...prev, lastName: e.target.value }))}
                        />
                      </div>
                      <div className="col-md-2 d-grid">
                        <button
                          type="button"
                          className="btn btn-outline-secondary"
                          onClick={searchTestUsers}
                          disabled={searchingTestUsers}
                        >
                          <FontAwesomeIcon icon={faSearch} className="me-1" />
                          {searchingTestUsers ? 'Searching…' : 'Find'}
                        </button>
                      </div>
                    </div>

                    {testUserResults.length > 0 && (
                      <div className="table-responsive mb-3">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr>
                              <th>User</th>
                              <th>Email</th>
                              <th className="text-end">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {testUserResults.map((user) => {
                              const attached = selectedTestUsers.some((entry) => entry._id === user._id);
                              return (
                                <tr key={user._id}>
                                  <td>{`${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Unnamed user'}</td>
                                  <td>{user.email}</td>
                                  <td className="text-end">
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() => attachTestUser(user)}
                                      disabled={attached || selectedTestUsers.length >= 5}
                                    >
                                      {attached ? 'Attached' : 'Attach'}
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
                      <div>
                        <div className="small fw-bold mb-1">Attached Users ({selectedTestUsers.length}/5)</div>
                        {selectedTestUsers.length ? (
                          <div className="d-flex flex-wrap gap-2">
                            {selectedTestUsers.map((user) => (
                              <span key={user._id} className="badge rounded-pill text-bg-secondary d-inline-flex align-items-center gap-2">
                                {`${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email}
                                <button
                                  type="button"
                                  className="btn-close btn-close-white"
                                  aria-label={`Remove ${user.email}`}
                                  onClick={() => removeTestUser(user._id)}
                                  style={{ fontSize: '0.55rem' }}
                                />
                              </span>
                            ))}
                          </div>
                        ) : (
                          <div className="text-muted small">No users attached yet.</div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={deployTestSurvey}
                        disabled={deployingTestSurvey || !selectedTestUsers.length}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                        {deployingTestSurvey ? 'Deploying…' : 'Deploy Immediately'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label fw-bold mb-0">Questions</label>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={addQuestion}
                  >
                    <FontAwesomeIcon icon={faPlus} className="me-1" />
                    Add question
                  </button>
                </div>

                {(canEditDetails || editingSurvey) && (
                  <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                    <select
                      className="form-select"
                      style={{ maxWidth: '420px' }}
                      value={libraryPickId}
                      onChange={(e) => setLibraryPickId(e.target.value)}
                      aria-label="Add existing library question"
                    >
                      <option value="">Add existing question from library…</option>
                      {unusedLibraryQuestions.map((question) => (
                        <option key={question._id} value={question._id}>
                          {question.prompt || 'Untitled question'}
                          {question.usedOnSurveyCount
                            ? ` (used on ${question.usedOnSurveyCount} survey${question.usedOnSurveyCount === 1 ? '' : 's'})`
                            : ''}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={addFromLibrary}
                      disabled={!libraryPickId}
                    >
                      Add from library
                    </button>
                  </div>
                )}

                {form.questions.length === 0 && (
                  <div className="text-muted small mb-2">No questions yet.</div>
                )}

                {form.questions.map((question, index) => {
                  const questionLocked = !canEditDetails && Boolean(question._id);
                  return (
                  <div key={question._id || `question-${index}`} className="deploy-test-task-editor">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        value={question.prompt}
                        onChange={(e) => updateQuestion(index, { prompt: e.target.value })}
                        placeholder={`Question ${index + 1}`}
                        disabled={questionLocked}
                        aria-label={`Question ${index + 1} prompt`}
                      />
                      <select
                        className="form-select flex-shrink-0"
                        style={{ width: '180px' }}
                        value={question.type}
                        disabled={questionLocked}
                        onChange={(e) => {
                          const type = e.target.value as SurveyQuestionType;
                          updateQuestion(index, {
                            type,
                            choices: type === 'choice'
                              ? (question.choices && question.choices.length > 0 ? question.choices : ['', ''])
                              : [],
                          });
                        }}
                        aria-label={`Question ${index + 1} type`}
                      >
                        {SURVEY_QUESTION_TYPE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => moveQuestion(index, -1)}
                        disabled={index === 0 || !canEditDetails}
                        aria-label="Move up"
                      >
                        <FontAwesomeIcon icon={faArrowUp} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => moveQuestion(index, 1)}
                        disabled={index === form.questions.length - 1 || !canEditDetails}
                        aria-label="Move down"
                      >
                        <FontAwesomeIcon icon={faArrowDown} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => removeQuestion(index)}
                        disabled={questionLocked}
                        aria-label="Remove question"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>

                    {question._id && (
                      <div className="text-muted small mb-2">
                        {questionLocked
                          ? 'Existing question — locked after this survey left Planning'
                          : 'Library question — edits update the shared definition'}
                        {!questionLocked && question.usedOnSurveyCount && question.usedOnSurveyCount > 1
                          ? ` (used on ${question.usedOnSurveyCount} surveys)`
                          : ''}
                        .
                      </div>
                    )}

                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      {question.isDefault ? (
                        <>
                          <span className="badge text-bg-secondary">
                            <FontAwesomeIcon icon={faBookmark} className="me-1" />
                            Default Question
                          </span>
                          {!questionLocked && (
                            <button
                              type="button"
                              className="btn btn-link btn-sm text-muted p-0"
                              onClick={() => updateQuestion(index, { isDefault: false })}
                            >
                              Remove default
                            </button>
                          )}
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => updateQuestion(index, { isDefault: true })}
                          disabled={questionLocked || !question.prompt.trim()}
                          title="Add question to library"
                          aria-label="Add question to library"
                        >
                          <FontAwesomeIcon icon={faBookmark} className="me-1" />
                          Add to Library
                        </button>
                      )}
                    </div>

                    {question.type === 'choice' && (
                      <div className="mb-2">
                        <label className="form-label fw-bold">Choices</label>
                        {(question.choices || []).map((choice, choiceIndex) => (
                          <div key={`${question._id || index}-choice-${choiceIndex}`} className="d-flex gap-2 mb-2">
                            <input
                              type="text"
                              className="form-control"
                              value={choice}
                              onChange={(e) => updateQuestionChoice(index, choiceIndex, e.target.value)}
                              placeholder={`Choice ${choiceIndex + 1}`}
                              disabled={questionLocked}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => removeQuestionChoice(index, choiceIndex)}
                              disabled={questionLocked || (question.choices || []).length <= 1}
                              aria-label="Remove choice"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline-secondary btn-sm"
                          onClick={() => addQuestionChoice(index)}
                          disabled={questionLocked}
                        >
                          <FontAwesomeIcon icon={faPlus} className="me-1" />
                          Add choice
                        </button>
                      </div>
                    )}
                    {question.type !== 'text' && (
                      <div>
                        <label className="form-label fw-bold">Additional Info</label>
                        <textarea
                          className="form-control"
                          rows={2}
                          value={question.additionalInfo || ''}
                          onChange={(e) => updateQuestion(index, { additionalInfo: e.target.value })}
                          placeholder="Optional extra prompt shown with this question"
                          disabled={questionLocked}
                        />
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="d-flex justify-content-between align-items-center">
              <div>
                {editingSurvey && canEditDetails && (
                  <button
                    type="button"
                    className="btn btn-outline-danger"
                    onClick={handleDelete}
                    disabled={saving || deleting}
                  >
                    {deleting ? 'Deleting…' : 'Delete'}
                  </button>
                )}
              </div>
              <div className="d-flex gap-2">
                <Link
                  to="/surveys"
                  className={`btn btn-secondary${saving || deleting ? ' disabled' : ''}`}
                >
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={saving || deleting}>
                  {saving ? 'Saving…' : 'Save'}
                </button>
              </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default SurveyEditorPage;
